import { Link, useNavigate } from "react-router-dom";
import "./HomeCommercial.css";
import { useContext, useEffect, useState, useCallback } from "react";
import { ArticleInfo } from "../../../Modeles/ArticleModel";
import AfficheArticleInMagasin from "../../../Services/Magasin/AfficheArticleInMagasin";
import { MagasinContext } from "../../../Context/MagasinContext";
import { NewSidebareCommercial } from "../../NewSideBareCommercial";
import axios from "axios";
import PanierLengVisiteur from "../../../Services/Panier/PanierLeng";
import defaultImg from "../../Admin/IMG/DefaulteImage.jpeg";
import EmptyCartPopup from "../../../components/EmptyCartPopup";
import Timer from "../../../components/Timer";

export interface ProductType {
  product: ArticleInfo[];
  messageErros: string;
}

type QtyMap = { [id: number]: number };
type AddedMap = { [id: number]: boolean };

export default function HomeCommercial() {
  const [selectedFilter, setSelectedFilter] = useState<string>("tout les produits");
  const [filterMoinsCher, setFilterMoinsCher] = useState(false);
  const [filterDisponible, setFilterDisponible] = useState(false);
  const magasinContext = useContext(MagasinContext);
  const MagasinId = localStorage.getItem("CommercialId");
  const [displayedProducts, setDisplayedProducts] = useState<ArticleInfo[]>([]); // kept for compat, unused
  const RemiseCommercial = localStorage.getItem("remise") || "";
  const remiseValue = parseFloat(RemiseCommercial.replace("%", "")) || 0;

  const CommercialName = localStorage.getItem("CommercialName");
  const MagasinName = localStorage.getItem("MagasinName");
  const SoldeCommercial = localStorage.getItem("SoldeCommercial");

  // Fix NaN: safely parse solde, fallback to 0
  const maxSolde = 10000;
  const parsedSolde = SoldeCommercial ? parseFloat(SoldeCommercial) : 0;
  const soldeValue = isNaN(parsedSolde) ? 0 : parsedSolde;
  const soldePercent = maxSolde > 0 ? Math.min((soldeValue / maxSolde) * 100, 100) : 0;

  const [products, setProducts] = useState<ArticleInfo[]>([]);
  const [alldata, setAlldata] = useState<ArticleInfo[]>([]); // kept for compat

  const id = MagasinId || magasinContext.id?.id;

  // Search: two states (same as HomeMagasin)
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [refs, setRefs] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [commissionRules, setCommissionRules] = useState<any[]>([]);

  /* ── cart (same as HomeMagasin — sends to backend) ── */
  const [qtyMap, setQtyMap] = useState<QtyMap>({});
  const [added, setAdded] = useState<AddedMap>({});
  const [addingId, setAddingId] = useState<number | null>(null);
  const [panierCount, setPanierCount] = useState<number>(0);
  const [panierExpiredMsg, setPanierExpiredMsg] = useState("");
  const [showEmptyCartPopup, setShowEmptyCartPopup] = useState(false);
  const navigate = useNavigate();

  /* ── fetch panier count + timer from backend ── */
  const fetchPanierCount = useCallback(async () => {
    const cid = MagasinId ?? "";
    if (!cid) return;
    try {
      const { data } = await PanierLengVisiteur(cid, "commercials").GetLenPanier();
      const items = Array.isArray(data) ? data : [];
      setPanierCount(items.length);
      // If panier became empty after having items, show expired message
      if (items.length === 0 && panierCount > 0) {
        setPanierExpiredMsg("Votre panier a expiré et a été vidé automatiquement.");
        setTimeout(() => setPanierExpiredMsg(""), 5000);
      }
    } catch { setPanierCount(0); }
  }, [MagasinId, panierCount]);

  // Unified CartTimer handles the expiration via localStorage

  /* ── listen for panier updates + initial fetch ── */
  useEffect(() => {
    fetchPanierCount();
    const handler = () => fetchPanierCount();
    window.addEventListener("panier-updated", handler);
    return () => window.removeEventListener("panier-updated", handler);
  }, [fetchPanierCount]);

  const getQty = (aid: number) => qtyMap[aid] ?? 1;
  const chQty = (aid: number, d: number) =>
    setQtyMap(p => ({ ...p, [aid]: Math.max(1, (p[aid] ?? 1) + d) }));
  const setQtyVal = (aid: number, v: string) => {
    const n = parseInt(v);
    if (!isNaN(n) && n >= 1) setQtyMap(p => ({ ...p, [aid]: n }));
  };

  /* ── fetch products (with pagination + append for load-more) ── */
  const fetchProducts = async (p = 1, append = false, sortParam = '', disponibleParam = false, filterMag = selectedFilter) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      const magParam = filterMag === 'Mes Produits' ? (MagasinName || '') : '';
      const res = await AfficheArticleInMagasin().getAnly20Article(p, sortParam, disponibleParam, magParam);
      setProducts(prev => append ? [...prev, ...res.data.data] : res.data.data);
      setPage(res.data.current_page);
      setTotalPages(res.data.total_pages);
      if (!append) setAlldata(res.data.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  /* ── fetch search results (with pagination + append) ── */
  const fetchRefs = async (term: string, p = 1, append = false, filterMag = selectedFilter) => {
    if (!term.trim()) { setRefs([]); setTotalPages(1); setPage(1); return; }
    try {
      append ? setLoadingMore(true) : setLoading(true);
      const magParam = filterMag === 'Mes Produits' ? (MagasinName || '') : '';
      const params: Record<string, string> = { q: term, page: String(p), perPage: '20' };
      if (magParam) params['magasinName'] = magParam;
      const r = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/search-articles`, { params });
      setRefs(prev => append ? [...prev, ...r.data.data] : r.data.data);
      setPage(r.data.current_page || r.data.page || p);
      setTotalPages(r.data.total_pages);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  /* ── reset everything to default product list ── */
  const resetToDefault = () => {
    setSearch("");
    setSearchInput("");
    setRefs([]);
    setPage(1);
    setTotalPages(1);
    localStorage.removeItem("SaveTape");
    fetchProducts(1, false, '', false, selectedFilter);
  };

  /* ── input change: if cleared → reset immediately ── */
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    if (val.trim() === "") resetToDefault();
  };

  /* ── commit search on icon click or Enter ── */
  const triggerSearch = () => {
    const val = searchInput.trim();
    if (val === "") { resetToDefault(); return; }
    setSearch(val);
    setRefs([]);
    setPage(1);
    localStorage.setItem("SaveTape", val);
    fetchRefs(val, 1, false, selectedFilter);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") triggerSearch();
  };

  /* ── Suivant: append next page ── */
  const handleSuivant = () => {
    if (page >= totalPages) return;
    const next = page + 1;
    const sortParam = filterMoinsCher ? 'price_asc' : '';
    if (search.trim() !== '') fetchRefs(search, next, true, selectedFilter);
    else fetchProducts(next, true, sortParam, filterDisponible, selectedFilter);
  };

  const hasMore = page < totalPages;

  /* ── fetch global commission ── */
  useEffect(() => {
    const fetchCommission = async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/commission`);
        setCommissionRules(r.data || []);
      } catch {
        setCommissionRules([]);
      }
    };
    fetchCommission();
  }, []);

  const getGlobalCommission = (): number => {
    const globalRule = commissionRules.find((r: any) => r.commission_type === "global");
    if (globalRule) return parseFloat(globalRule.commission_value) || 0;
    return 0;
  };

  const getMagasinCommission = (nomMagasin?: string): number => {
    if (!nomMagasin) return 0;
    const magasinRule = commissionRules.find(
      (r: any) =>
        r.commission_type === "magasin_all" &&
        r.source_magasin_name?.toLowerCase() === nomMagasin?.toLowerCase()
    );
    if (magasinRule) return parseFloat(magasinRule.commission_value) || 0;
    return 0;
  };

  /* ── Re-fetch from page 1 on mount & whenever filter buttons change (server-side filtering) ── */
  useEffect(() => {
    const sortParam = filterMoinsCher ? 'price_asc' : '';
    setPage(1);
    setRefs([]);
    if (search.trim() !== '') {
      fetchRefs(search, 1, false, selectedFilter);
    } else {
      fetchProducts(1, false, sortParam, filterDisponible, selectedFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMoinsCher, filterDisponible, selectedFilter]);

  const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  /* ── unified display list: search results OR default products ── */
  const list = search.trim() !== "" ? refs : products;

  /* ── add to cart (same as HomeMagasin — sends to backend) ── */
  const addToCart = async (pro: any, e?: React.MouseEvent) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const aid = pro.IdArticle ?? pro.id;
    const qty = getQty(aid);
    const commercialId = MagasinId ?? "";
    setAddingId(aid);
    const remise = parseFloat((localStorage.getItem("remise") || "0").replace("%", "")) || 0;
    const commissionWyn = getGlobalCommission();
    const pHT = parseFloat(String(pro.PrixNetHT ?? "0").replace(",", ".").trim()) || 0;
    const oldPrice = remise > 0
      ? pHT * (1 + commissionWyn / 100) * (1 + remise / 100)
      : pHT * (1 + commissionWyn / 100);
    const finalPrice = remise > 0
      ? oldPrice * (1 - remise / 100)
      : oldPrice;

    try {
      const fd = new FormData();
      fd.append("IdUtilisateur",   commercialId);
      fd.append("TypeUtilisiteur", "commercials");
      fd.append("IdArticle",       String(aid));
      fd.append("RefArticle",      pro.RefArticle);
      fd.append("Designation",     pro.Designation);
      fd.append("Image",           pro.urlImage || defaultImg || "");
      fd.append("price",           finalPrice.toFixed(2));
      fd.append("quantity",        String(qty));
      fd.append("NomMagasin",      pro.NomMagasin);
      await axios.post(`${process.env.REACT_APP_PHP_APP_URL}/panier`, fd);
      localStorage.setItem('cartExpiration', String(Date.now() + 50 * 60 * 1000)); // 50 minutes
      setAdded(p => ({ ...p, [aid]: true }));
      setTimeout(() => setAdded(p => ({ ...p, [aid]: false })), 1800);
      await PanierLengVisiteur(commercialId, "commercials").GetLenPanier();
      // Signal the sidebar to refresh its panier count
      window.dispatchEvent(new Event("panier-updated"));
    } catch (e) { console.error(e); } finally { setAddingId(null); }
  };

  const ProductCard = ({ pro }: { pro: any }) => {
    const remise = parseFloat((localStorage.getItem("remise") || "0").replace("%", "")) || 0;
    const commissionWyn = getGlobalCommission();
    const prixHT = parseFloat(String(pro.PrixNetHT ?? pro.PrixVenteArticleTTC ?? pro.PrixVente ?? pro.Prix ?? "0").replace(",", ".").trim()) || 0;
    const isMine = pro.NomMagasin === MagasinName;

    // Price logic:
    // old_price (barré) = prixHT * (1 + commissionWyn/100) * (1 + remise/100)
    // new_price (actual) = old_price * (1 - remise/100)
    const oldPrice = remise > 0
      ? prixHT * (1 + commissionWyn / 100) * (1 + remise / 100)
      : prixHT * (1 + commissionWyn / 100);
    const newPrice = remise > 0
      ? oldPrice * (1 - remise / 100)
      : oldPrice;

    const aid = pro.IdArticle ?? pro.id;
    const isAdded = added[aid] ?? false;
    const isAdding = addingId === aid;

    const parseQty = (val: any): number | null => {
      if (val === undefined || val === null || val === "") return null;
      const clean = String(val).replace(",", ".").trim();
      if (clean === "") return null;
      const n = parseFloat(clean);
      return isNaN(n) ? null : n;
    };
    const availableQty = parseQty(pro.Qte) 
      ?? parseQty(pro.Quantité) 
      ?? parseQty(pro.quantite) 
      ?? parseQty(pro.stock) 
      ?? parseQty(pro.quantity) 
      ?? 0;
    const outOfStock = availableQty <= 0;

    return (
      <Link
        className={`product-link-nd-commercial-u ${outOfStock ? "u-card-nostock-commercial-u" : ""}`}
        to={`/commercials/${id}/${pro.NomMagasin}/articles/${btoa(String(pro.RefArticle))}`}
      >
        <div className="u-card-nd-commercial-u">
          {remise > 0 && prixHT > 0 && (
            <div className="u-off-nd-commercial-u">
              <span className="off-nd-commercial-u">-{remise}% remise</span>
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <img 
              src={pro.urlImage || defaultImg} 
              alt={pro.Designation} 
              className={`u-card-img-nd-commercial-u ${outOfStock ? "u-img-grey-commercial-u" : ""}`} 
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultImg;
              }}
            />
            {outOfStock && (
              <div className="u-stock-badge-commercial-u u-stock-out-commercial-u">
                <i className="bi bi-x-circle-fill"></i> Stock épuisé
              </div>
            )}
          </div>

          <div className="u-card-icons-nd-commercial-u">
            <i className="bi bi-suit-heart u-heart-nd-commercial-u"></i>
          </div>

          <div className="u-card-body-nd-commercial-u">
            <div className="u-star-nd-commercial-u">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="bi bi-star-fill u-i-star-nd-commercial-u"></i>
              ))}
            </div>

            {userLocation && pro.latitude && pro.longitude && (
              <div className="u-km-nd-commercial-u">
                <i className="bi bi-geo-alt-fill"></i>
                À {Math.round(getDistance(userLocation.latitude, userLocation.longitude, pro.latitude, pro.longitude))} km
              </div>
            )}

            <div className="u-card-title-nd-commercial-u">
              {pro.Designation}
              {pro.DesignationAr ? ` | ${pro.DesignationAr}` : ""}
            </div>

            <p className="u-card-ref-nd-commercial-u" title={`Réf: ${pro.RefArticle || pro.ref || pro.Reference || pro.reference || ''}`}>
              <i className="bi bi-hash" style={{ color: "#F5A623", marginRight: "2px" }}></i>
              Réf : {pro.RefArticle || pro.ref || pro.Reference || pro.reference || "N/A"}
            </p>

            {/* Price display logic */}
            {remise > 0 && prixHT > 0 ? (
              <div className="u-card-price-block-nd-commercial-u">
                {/* crossed-out price (old price) */}
                <span className="u-card-price-crossed-nd-commercial-u">
                  {oldPrice.toFixed(2)} MAD
                </span>
                {/* actual price (new price) */}
                <span className="u-card-price-nd-commercial-u">
                  {newPrice.toFixed(2)} MAD
                </span>
              </div>
            ) : (
              <div className="u-card-price-nd-commercial-u">
                {newPrice.toFixed(2)} MAD
              </div>
            )}

            {/* Add to cart button */}
            {!outOfStock && (
              <button
                className={`u-card-add-btn-nd-commercial-u ${isAdded ? "u-card-added-nd-commercial-u" : ""}`}
                onClick={(e) => addToCart(pro, e)}
                disabled={isAdding}
              >
                {isAdding ? (
                  <><span className="btn-spinner-commercial" /> Ajout...</>
                ) : isAdded ? (
                  <><i className="bi bi-check-lg"></i> Ajouté</>
                ) : (
                  <><i className="bi bi-cart-plus"></i> Ajouter au panier</>
                )}
              </button>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const LoyaltyCard = () => (
    <div className="card-cart-solde-nd-commercial-u">
      <div className="card__info-nd-commercial-u">
        <div className="card__chip-nd-commercial-u">
          <svg className="card__chip-lines-nd-commercial-u" role="img" width="100%" height="100%" viewBox="0 0 100 100" aria-label="Chip">
            <g opacity="0.8">
              <polyline points="0,50 35,50" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="0,20 20,20 35,35" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="50,0 50,35" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="65,35 80,20 100,20" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="100,50 65,50" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="35,35 65,35 65,65 35,65 35,35" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="0,80 20,80 35,65" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="50,100 50,65" fill="none" stroke="#000" strokeWidth="2" />
              <polyline points="65,65 80,80 100,80" fill="none" stroke="#000" strokeWidth="2" />
            </g>
          </svg>
          <div className="card__chip-texture-nd-commercial-u"></div>
        </div>

        <div className="card__number-nd-commercial-u">
          <span className="card__digit-group-nd-commercial-u">0000</span>
          <span className="card__digit-group-nd-commercial-u">0021</span>
          <span className="card__digit-group-nd-commercial-u">4748</span>
          <span className="card__digit-group-nd-commercial-u">3647</span>
        </div>

        <div className="card__valid-thru-nd-commercial-u">Carte Fidélité</div>
        <div className="card__exp-date-nd-commercial-u"><time dateTime="2026-01">01/26</time></div>
        <div className="card__name-nd-commercial-u">{CommercialName}</div>
      </div>

      <div className="card-badge-nd-commercial-u">Premium</div>
      <div className="card__texture-nd-commercial-u"></div>
    </div>
  );

  return (
    <div className="wrapper-nd-commercial-u">
      <EmptyCartPopup show={showEmptyCartPopup} onClose={() => setShowEmptyCartPopup(false)} />
      <NewSidebareCommercial />

      <main className="main-content-nd-commercial-u">
        {/* Header */}
        <div className="header-nd-commercial-u">
          <div>
            <h1 className="header-title-nd-commercial-u">
              Bienvenue, <span>{CommercialName}</span>
            </h1>
            <p className="header-subtitle-nd-commercial-u">
              Connecté sur le magasin <strong>{MagasinName}</strong>
            </p>
          </div>
        </div>

        {/* Top Row */}
        <div className="top-row-nd-commercial-u">
          {/* Left: Search + Filters */}
          <div className="left-panel-nd-commercial-u">
            <div className="search-wrapper-nd-commercial-u">
              <i
                className="bi bi-search search-icon-nd-commercial-u"
                style={{ cursor: "pointer" }}
                onClick={triggerSearch}
              ></i>
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                className="search-input-nd-commercial-u"
                placeholder="Rechercher un produit, référence..."
              />
              {searchInput && (
                <button className="search-clear-nd-commercial-u" onClick={resetToDefault}>
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>

            <div className="filter-section-nd-commercial-u">
              <div className="filter-label-nd-commercial-u">
                <i className="bi bi-funnel-fill"></i>
                Filtrer par magasin
              </div>
              <div className="filter-chips-nd-commercial-u">
                {["tout les produits", "Mes Produits"].map((f) => (
                  <button
                    key={f}
                    className={`chip-nd-commercial-u ${selectedFilter === f ? "active-chip-nd-commercial-u" : ""}`}
                    onClick={() => setSelectedFilter(f)}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Moins cher / En stock filter buttons */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setFilterMoinsCher(v => !v)}
                className={`chip-nd-commercial-u ${filterMoinsCher ? 'active-chip-nd-commercial-u' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <i className="bi bi-graph-down-arrow" />
                Moins chers
                {filterMoinsCher && <i className="bi bi-check-lg" />}
              </button>
              <button
                onClick={() => setFilterDisponible(v => !v)}
                className={`chip-nd-commercial-u ${filterDisponible ? 'active-chip-nd-commercial-u' : ''}`}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <i className="bi bi-box-seam" />
                En stock
                {filterDisponible && <i className="bi bi-check-lg" />}
              </button>
            </div>
          </div>

          {/* Right: Card + Solde */}
          <div className="card-panel-nd-commercial-u">
            <LoyaltyCard />

            <div className="solde-card-nd-commercial-u">
              <div className="solde-header-nd-commercial-u">
                <span className="solde-title-nd-commercial-u">
                  <i className="bi bi-graph-up-arrow"></i>
                  Solde disponible
                </span>
                <span className="solde-amount-nd-commercial-u">
                  {soldeValue.toLocaleString("fr-FR")} <span>/ {maxSolde.toLocaleString("fr-FR")} MAD</span>
                </span>
              </div>
              <div className="progress-track-nd-commercial-u">
                <div
                  className="progress-fill-nd-commercial-u"
                  style={{ width: `${isNaN(soldePercent) ? 0 : soldePercent}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="section-header-nd-commercial-u">
          <h2 className="section-title-nd-commercial-u">Équipements</h2>
          <Link to={`/commercials/${id}/articles`} className="voir-plus-nd-commercial-u">
            Voir plus <i className="bi bi-arrow-right"></i>
          </Link>
        </div>

        {loading ? (
          <div className="loading-nd-commercial-u">
            <div className="spinner-nd-commercial-u"></div>
            <span className="loader-text-nd-commercial-u">Chargement...</span>
          </div>
        ) : list.length > 0 ? (
          <>
            <div className="product-grid-nd-commercial-u">
              {list.map((pro) => (
                <ProductCard key={`${pro.id || pro.IdArticle}-${pro.RefArticle}`} pro={pro} />
              ))}
            </div>
            {hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0 10px' }}>
                <button
                  onClick={handleSuivant}
                  disabled={loadingMore}
                  className="chip-nd-commercial-u active-chip-nd-commercial-u"
                  style={{ padding: '12px 36px', fontSize: '0.88rem', gap: '8px', display: 'flex', alignItems: 'center' }}
                >
                  {loadingMore
                    ? <><span className="spinner-nd-commercial-u" style={{ width: 16, height: 16, borderWidth: 2 }}></span> Chargement…</>
                    : <><i className="bi bi-arrow-down-circle"></i> Suivant</>}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-produit-nd-commercial-u">
            <div className="no-produit-icon-nd-commercial-u">
              <i className="bi bi-search"></i>
            </div>
            <h5>Aucun résultat trouvé</h5>
            <p>Essayez un mot similaire ou quelque chose de plus général.</p>
          </div>
        )}
      </main>

      {/* ===== PANIER EXPIRED TOAST ===== */}
      {panierExpiredMsg && (
        <div className="panier-expired-toast-nd-commercial-u">
          <i className="bi bi-exclamation-triangle-fill"></i>
          {panierExpiredMsg}
        </div>
      )}

      {/* ===== FIXED FLOATING ICONS ===== */}
      <div className="floating-actions-nd-commercial-u">
        {panierCount > 0 && (
          <div className="floating-timer-nd-commercial-u">
            <Timer userId={MagasinId || ""} userType="commercial" asFloating={false} />
          </div>
        )}
        <button
          className="floating-btn-nd-commercial-u floating-cart-nd-commercial-u"
          style={{ border: 'none', cursor: 'pointer' }}
          onClick={() => panierCount === 0 ? setShowEmptyCartPopup(true) : navigate(`/commercials/${id}/panier`)}
        >
          <i className="bi bi-cart3"></i>
          {panierCount > 0 && <span className="floating-cart-badge-nd-commercial-u">{panierCount}</span>}
        </button>
        <Link to={`/commercials/${id}/assistance`} className="floating-btn-nd-commercial-u floating-support-nd-commercial-u">
          <i className="bi bi-headset"></i>
        </Link>
      </div>
    </div>
  );
}